import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  CardTitle,
  CardSubtitle,
  CardText,
  Badge,
  Alert,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  ListGroup,
  ListGroupItem,
  Spinner,
  Table,
  Collapse,
  Button,
  CardHeader,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
// import ItemServices from "../../../services/DibyaServices/ItemServices/ItemServices";
import restro from "../../../assets/orderingSystem/restrologo.png";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [activeSideTab, setActiveSideTab] = useState("0");
  const [activeAddonTab, setActiveAddonTab] = useState("addOns");
  const [collapseStates, setCollapseStates] = useState({});
  const [allData, setAllData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Format date to 'YYYY-MM-DD'
  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  // Format transaction date (assuming formatDateTimes is defined elsewhere)
  const formatDateTimes = (date) =>
    date ? new Date(date).toLocaleString("en-US") : "N/A";

  // Toggle functions
  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleSideTab = (tab) => {
    if (activeSideTab !== tab) setActiveSideTab(tab);
  };

  const toggleAddonTab = (tab) => {
    if (activeAddonTab !== tab) setActiveAddonTab(tab);
  };

  const toggleCollapse = (sideName) => {
    setCollapseStates((prev) => ({
      ...prev,
      [sideName]: !prev[sideName],
    }));
  };

  // Fetch product details
  // useEffect(() => {
    const fetchItemDetail = async () => {
      setLoading(true);
      try {
        const response = await ItemServices.productDetail(id);
        setProduct(response);
        // Initialize collapse states for side details
        if (response.sideDetails?.length > 0) {
          const initialCollapseStates = {};
          response.sideDetails.forEach((side) => {
            initialCollapseStates[side.sideName] = false;
          });
          setCollapseStates(initialCollapseStates);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching product details:", err);
        setError("Failed to load product details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItemDetail();
    } else {
      setError("Invalid product ID.");
      setLoading(false);
    }
  // }, [id]);

  // Fetch transaction data
  const fetchData = async (from = fromDate, to = toDate) => {
    if (!from || !to) {
      setError("Please select both From and To dates");
      return;
    }

    setLoading(true);
    try {
      const response = await SalesReportService.getTransactionList({
        productId: id,
        fromDate: formatDate(from),
        toDate: formatDate(to),
      });
      setAllData(response || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching transaction data:", error);
      setError("Failed to load transaction data. Please try again.");
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  // Set default date range (last 1 month) and fetch data on mount
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    fetchData(lastMonth, today);
  }, [id]); // Depend on id to refetch if it changes

  // Table columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "75px",
    },
    {
      name: "Transaction Date",
      selector: (row) => formatDateTimes(row?.transactionDate),
      sortable: true,
      width: "14vw",
    },
    // { name: "Product", selector: (row) => row.productName, sortable: true },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      sortable: true,
      right: true,
    },
    {
      name: "Unit Price",
      selector: (row) => row.unitPrice,
      sortable: true,
      right: true,
    },

    {
      name: "Debit",
      selector: (row) => row.debit,
      sortable: true,
      right: true,
    },
    {
      name: "Credit",
      selector: (row) => row.credit,
      sortable: true,
      right: true,
    },
    {
      name: "Total",
      selector: (row) => row.totalAmount,
      sortable: true,
      right: true,
    },
  ];

  // Early return for loading state
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading product details...</p>
      </div>
    );
  }

  // Early return for error state
  if (error) {
    return (
      <Alert color="danger" className="my-4">
        <h5 className="alert-heading">Error!</h5>
        <p className="mb-0">{error}</p>
        <Button
          color="primary"
          className="mt-3"
          onClick={() => {
            setError(null);
            fetchData(fromDate, toDate);
          }}
        >
          Retry
        </Button>
      </Alert>
    );
  }

  // Early return if no product found
  if (!product) {
    return (
      <Alert color="warning" className="my-4">
        <h5 className="alert-heading">Product Not Found</h5>
        <p className="mb-0">
          The requested product could not be found or may have been removed.
        </p>
      </Alert>
    );
  }

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Product"
          title="Product Detail"
          breadcrumbItems={breadcrumbItems}
        />
      </div>
      <Row>
        {/* Product Image Column */}
        <Col lg={5} className="">
          <Card className="border-0 shadow">
            <div className="position-relative d-flex justify-content-center align-content-center align-items-center">
              {/* <div className="position-absolute top-0 end-0 m-3 d-flex justify-content-end gap-3">
                {product?.isCatering && (
                  <Badge color="success" className="p-1">
                    CATERING
                  </Badge>
                )}
                {product?.isPackage && (
                  <Badge color="warning" className="p-1">
                    PACKAGE
                  </Badge>
                )}
              </div> */}

              {product.productImages && product.productImages.length > 0 ? (
                <img
                  // src={`${window.APP_CONFIG.api1.baseapiV2}/${product.productImages[0]?.imagePath}`}
                  src={product?.productImages[0]?.cloudR2ImagePath.getCDNUrl()}
                  alt={product.name}
                  className="img-fluid rounded"
                  style={{ objectFit: "contain" }}
                  onError={(e) => {
                    if (!e.target.dataset.error) {
                      e.target.dataset.error = "true";
                      e.target.src = "/trending-product.jpg";
                    }
                  }}
                />
              ) : (
                <img
                  src={restro}
                  alt="Default Product"
                  className="img-fluid rounded"
                  style={{ objectFit: "contain" }}
                />
              )}
            </div>

            {/* Additional Images Row (if multiple images exist) */}
            {product.productImages && product.productImages.length > 1 && (
              <CardBody className="pt-3">
                <h6 className="fw-semibold mb-2">More Views</h6>
                <Row className="g-2">
                  {product.productImages.slice(0, 4).map((image, index) => (
                    <Col xs={3} key={image.id || index}>
                      <img
                        // src={`${window.APP_CONFIG.api1.baseapiV2}/${image.imagePath}`}
                        src={product?.productImages[0]?.cloudR2ImagePath.getCDNUrl()}
                        alt={`Product view ${index + 1}`}
                        className="img-thumbnail"
                        style={{
                          width: "100%",
                          height: "60px",
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onError={(e) => {
                          if (!e.target.dataset.error) {
                            e.target.dataset.error = "true";
                            e.target.src = "/trending-product.jpg";
                          }
                        }}
                      />
                    </Col>
                  ))}
                </Row>
              </CardBody>
            )}
          </Card>
        </Col>

        {/* Product Details Column */}
        <Col lg={7}>
          <Card className="border-0 shadow ">
            <CardBody>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <CardTitle className="mb-1 text-primary fs-4 fw-semibold">
                    {product.name}
                  </CardTitle>
                  {/* <CardSubtitle tag="h6" className="text-muted mb-0">
                    Category:{" "}
                    <span className="fw-bold">{product.category}</span>
                  </CardSubtitle> */}
                </div>
                <Badge color="success" pill className="px-3 py-2 fs-6">
                  Rs.{product.price.toFixed(2)}
                </Badge>
              </div>

              <div className="mb-4 bg-light p-3 rounded">
                <h5 className="border-bottom pb-2 mb-3">Description</h5>
                <CardText>
                  {product.description || "No description available."}
                </CardText>
              </div>

              {/* Product Details Tabs */}
              <div className="mt-4">
                <Nav tabs>
                  <NavItem className="cursor-pointer">
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggle("1")}
                    >
                      Details
                    </NavLink>
                  </NavItem>
                  {product.tags && (
                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => toggle("2")}
                      >
                        Tags
                      </NavLink>
                    </NavItem>
                  )}
                  {product.attributes && product.attributes.length > 0 && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "3" })}
                        onClick={() => toggle("3")}
                      >
                        Attributes
                      </NavLink>
                    </NavItem>
                  )}
                  {(product.addons?.addOns?.length > 0 ||
                    product.addons?.extras?.length > 0) && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "4" })}
                        onClick={() => toggle("4")}
                      >
                        Add-ons
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
                <TabContent
                  activeTab={activeTab}
                  className="p-3 border border-top-0 rounded-bottom"
                >
                  <TabPane tabId="1">
                    <Row>
                      <Col sm={6}>
                        <ListGroup flush>
                          <ListGroupItem className="px-0 border-0">
                            <span className="fw-medium">Name:</span>{" "}
                            {product.name}
                          </ListGroupItem>
                          <ListGroupItem className="px-0 border-0">
                            <span className="fw-medium">Category:</span>{" "}
                            {product.categoryId}
                          </ListGroupItem>
                          <ListGroupItem className="px-0 border-0">
                            <span className="fw-medium">Price:</span> Rs.{" "}
                            {product.price.toFixed(2)}
                          </ListGroupItem>
                        </ListGroup>
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="2">
                    {product.tags ? (
                      <div className="d-flex flex-wrap gap-2">
                        {product.tags.split(",").map((tag, index) => (
                          <Badge key={index} color="info" className="px-3 py-2">
                            {tag.trim()}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">
                        No tags available for this product.
                      </p>
                    )}
                  </TabPane>
                  <TabPane tabId="3">
                    {product.attributes && product.attributes.length > 0 ? (
                      <ListGroup flush className="mb-0">
                        {product.attributes.map((attr, index) => (
                          <ListGroupItem
                            key={index}
                            className="bg-transparent px-0"
                          >
                            <Row className="align-items-center">
                              <Col xs={4} md={4} className="text-muted">
                                {attr.title}
                              </Col>
                              <Col xs={4} md={4} className="text-center">
                                <Badge>{attr.attributeType}</Badge>
                              </Col>
                              <Col xs={4} md={4} className="text-end fw-medium">
                                Rs.{attr.value}
                              </Col>
                            </Row>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                    ) : (
                      <p className="text-muted">No attributes available</p>
                    )}
                  </TabPane>
                  <TabPane tabId="4">
                    {/* Add-ons and Extras Tab Content */}
                    <Nav tabs>
                      {product.addons?.addOns?.length > 0 && (
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeAddonTab === "addOns",
                            })}
                            onClick={() => toggleAddonTab("addOns")}
                          >
                            Add-ons
                          </NavLink>
                        </NavItem>
                      )}
                      {product.addons?.extras?.length > 0 && (
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeAddonTab === "extras",
                            })}
                            onClick={() => toggleAddonTab("extras")}
                          >
                            Extras
                          </NavLink>
                        </NavItem>
                      )}
                    </Nav>
                    <TabContent activeTab={activeAddonTab} className="mt-3">
                      <TabPane tabId="addOns">
                        {product.addons?.addOns?.length > 0 ? (
                          <Table bordered>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {product.addons.addOns.map((addon, index) => (
                                <tr key={index}>
                                  <td>{addon.name}</td>
                                  <td>Rs.{addon.price.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <p className="text-muted">No add-ons available</p>
                        )}
                      </TabPane>
                      <TabPane tabId="extras">
                        {product.addons?.extras?.length > 0 ? (
                          <Table bordered>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {product.addons.extras.map((extra, index) => (
                                <tr key={index}>
                                  <td>{extra.name}</td>
                                  <td>Rs.{extra.price.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <p className="text-muted">No extras available</p>
                        )}
                      </TabPane>
                    </TabContent>
                  </TabPane>
                </TabContent>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Child Products Section (if any) */}
        {product.childProductDtos && product.childProductDtos.length > 0 && (
          <Col lg={12} className="">
            <Card className="border-0 shadow">
              <CardBody>
                <h4 className="mb-4">Included Products</h4>
                <Row className="g-3">
                  {product.childProductDtos.map((item, index) => (
                    <Col md={3} sm={6} key={item.id || index}>
                      <Card className="h-100 border">
                        <img
                          // src={
                          //   item.childProduct.productImages &&
                          //   item.childProduct.productImages.length > 0
                          //     ? `${window.APP_CONFIG.api1.baseapiV2}/${item.childProduct.productImages[0].imagePath}`
                          //     : "/trending-product.jpg"
                          // }

                          src={item?.childProduct?.productImages[0]?.cloudR2ImagePath.getCDNUrl()}
                          // src={
                          //   item.childProduct.productImages &&
                          //   item.childProduct.productImages.length > 0
                          //     ? `${window.APP_CONFIG.api1.baseapiV2}/${item.childProduct.productImages[0].imagePath}`
                          //     : "/trending-product.jpg"
                          // }

                          alt={item.childProduct.name}
                          className="card-img-top"
                          style={{ height: "160px", objectFit: "cover" }}
                          onError={(e) => {
                            if (!e.target.dataset.error) {
                              e.target.dataset.error = "true";
                              e.target.src = "/trending-product.jpg";
                            }
                          }}
                        />
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-1">{item.childProduct.name}</h6>
                            <Badge color="success" className="fw-bold ">
                              ${item.price.toFixed(2)}
                            </Badge>
                          </div>
                          <CardText className="text-muted small mb-0">
                            {item.childProduct.category}
                          </CardText>
                          <div className="mt-2">
                            <span className="fw-bold ">
                              {item.childProduct.description}
                            </span>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {/* Side Items Section */}
      {product.sideDetails && product.sideDetails.length > 0 && (
        <Card className="mt-4 p-3">
          <CardBody>
            <h5 className="border-bottom pb-2 mb-3">Sides</h5>
            <Nav tabs>
              {product.sideDetails.map((side, index) => (
                <NavItem key={index} className="cursor-pointer">
                  <NavLink
                    className={classnames({
                      active: activeSideTab === index.toString(),
                    })}
                    onClick={() => toggleSideTab(index.toString())}
                  >
                    {side.sideName}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent
              activeTab={activeSideTab}
              className="p-3 border border-top-0 rounded-bottom"
            >
              {product.sideDetails.map((side, index) => (
                <TabPane key={index} tabId={index.toString()}>
                  <div className="text-end w-100 text-primary text-decoration-underline">
                    choosable option : {side.noOfSidesChoosable}
                  </div>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th className="col-lg-8 col-md-6 col-sm-auto">
                          Description
                        </th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {side.sideItems.map((item, itemIndex) => (
                        <tr key={itemIndex}>
                          <td>
                            <div className="d-flex align-items-center">
                              {item.productDetail.productImages &&
                                item.productDetail.productImages.length > 0 && (
                                  <img
                                    // src={`${window.APP_CONFIG.api1.baseapiV2}/${item.productDetail.productImages[0].imagePath}`}
                                    src={item?.productDetail?.productImages[0]?.cloudR2ImagePath.getCDNUrl()}
                                    alt={item.productDetail.name}
                                    className="rounded me-2"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      objectFit: "cover",
                                    }}
                                  />
                                )}
                              {item.productDetail.name}
                            </div>
                          </td>
                          <td>{item.productDetail.description || "N/A"}</td>
                          <td>Rs.{item.price.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TabPane>
              ))}
            </TabContent>
          </CardBody>
        </Card>
      )}
      <hr />
      {/* Filters */}
      <Row className="mb-2 align-items-end">
        <Col>
          <h4>Transaction Logs</h4>
        </Col>
        <Col md={2} className="mb-2">
          <Flatpickr
            options={{ dateFormat: "Y-m-d" }}
            value={fromDate}
            onChange={(dates) => setFromDate(dates[0])}
            className="form-control"
            placeholder="From Date"
          />
        </Col>
        <Col md={2} className="mb-2">
          <Flatpickr
            options={{ dateFormat: "Y-m-d" }}
            value={toDate}
            onChange={(dates) => setToDate(dates[0])}
            className="form-control"
            placeholder="To Date"
          />
        </Col>
        <Col md={2} className="mb-2">
          <Button color="primary" className="w-100" onClick={() => fetchData()}>
            Generate
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="my-5 text-center d-flex flex-column align-items-center">
          <Triangle
            visible={true}
            height="80"
            width="80"
            color="#5B71B9"
            ariaLabel="triangle-loading"
          />
          <h5 className="mt-3">Loading...</h5>
        </div>
      ) : (
        <Card>
          <CardBody>
            <CustomDataTable
              responsive
              striped
              pagination
              fixedHeader
              persistTableHead
              progressPending={loading}
              columns={columns}
              data={allData}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default ItemDetail;
